import { useEffect, useRef, useState } from "react";
import { useControl } from "../control/ControlContext";
import { useDatabase } from "../database/DatabaseContext";
import type { DBPool } from "../duckdb";

async function fetchDateRange(dbPool: DBPool): Promise<{ min: Date; max: Date } | null> {
  const db = dbPool.getDB();
  const conn = await db.connect();
  try {
    const result = await conn.query("SELECT MIN(bid_date) as min_date, MAX(bid_date) as max_date FROM aggregated_data");
    const rows = result.toArray();
    if (rows.length === 0 || !rows[0].min_date || !rows[0].max_date) return null;
    return { min: new Date(rows[0].min_date), max: new Date(rows[0].max_date) };
  } finally {
    await conn.close();
  }
}

function formatISODate(value: Date | null): string {
  if (!value) return "";
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, "0");
  const day = `${value.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseISODate(value: string): Date | null {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export default function TimeFilter() {
  const { dbPool } = useDatabase();
  const { timeRange, setTimeRange } = useControl();
  const [availableRange, setAvailableRange] = useState<{ min: Date; max: Date } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (!dbPool || hasInitialized.current) return;
    setIsLoading(true);
    fetchDateRange(dbPool)
      .then(range => {
        setAvailableRange(range);
        if (range && (!timeRange.start || !timeRange.end)) {
          setTimeRange({ start: range.min, end: range.max });
        }
        hasInitialized.current = true;
      })
      .finally(() => setIsLoading(false));
  }, [dbPool, setTimeRange, timeRange.start, timeRange.end]);

  if (isLoading) {
    return (
      <div className="time-filter">
        <span>Loading date range...</span>
      </div>
    );
  }

  if (!availableRange) {
    return (
      <div className="time-filter">
        <span>No date range available</span>
      </div>
    );
  }

  return (
    <div className="time-filter">
      <label>
        Start
        <input
          type="date"
          min={formatISODate(availableRange.min)}
          max={formatISODate(availableRange.max)}
          value={formatISODate(timeRange.start)}
          onChange={event => setTimeRange({ ...timeRange, start: parseISODate(event.target.value) })}
        />
      </label>
      <label>
        End
        <input
          type="date"
          min={formatISODate(availableRange.min)}
          max={formatISODate(availableRange.max)}
          value={formatISODate(timeRange.end)}
          onChange={event => setTimeRange({ ...timeRange, end: parseISODate(event.target.value) })}
        />
      </label>
      {(formatISODate(timeRange.start) !== formatISODate(availableRange.min) ||
        formatISODate(timeRange.end) !== formatISODate(availableRange.max)) && (
        <button
          type="button"
          title="Reset to full date range"
          onClick={() => setTimeRange({ start: availableRange.min, end: availableRange.max })}
        >
          &#x21ba;
        </button>
      )}
    </div>
  );
}
